import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { buildAppMeta } from "@/lib/seo";
import {
  type PublicBookingPage,
  type PublicDaySlots,
  fmtSlotTime,
  fmtBookingDate,
  isoDatePlus,
  googleCalendarUrl,
  icsFileContent,
  WEEKDAY_LABELS,
} from "@/lib/booking";
import { submitPublicBooking, type SubmitBookingResult } from "@/lib/booking.functions";
import { MapPin, CalendarCheck2, ChevronLeft, ChevronRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/book/$token")({
  head: () => ({
    meta: buildAppMeta({
      title: "Book a lesson",
      description: "Reserve a coaching session.",
      path: "/book",
    }),
  }),
  component: PublicBookingPage_,
});

const ERROR_COPY: Record<string, string> = {
  slot_taken: "That time was just taken — please pick another slot.",
  slot_unavailable: "That time is no longer available — please pick another slot.",
  too_many_requests: "Too many attempts. Please wait a few minutes and try again.",
  too_many_bookings: "You already have several upcoming bookings with this coach.",
  existing_clients_only: "This coach is only taking bookings from existing clients right now.",
  invalid_input: "Please check your name and email and try again.",
  page_unavailable: "This booking page is no longer active.",
  server_error: "Something went wrong. Please try again.",
};

const DAYS_PER_VIEW = 7;

function PublicBookingPage_() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const submit = useServerFn(submitPublicBooking);

  const [locationId, setLocationId] = useState<string | null>(null);
  const [dayOffset, setDayOffset] = useState(0); // paging through days, step = DAYS_PER_VIEW
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{ start: string; end: string } | null>(null);

  // Form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState<
    Extract<SubmitBookingResult, { ok: true }>["booking"] | null
  >(null);

  const pageQ = useQuery({
    queryKey: ["public-booking-page", token],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_booking_page", { _token: token });
      if (error) throw error;
      return (data as unknown as PublicBookingPage | null) ?? null;
    },
    staleTime: 60 * 1000,
    retry: 1,
  });

  const page = pageQ.data ?? null;
  const activeLocation =
    page?.locations.find((l) => l.id === locationId) ??
    (page?.locations.length === 1 ? page.locations[0] : null);

  const slotsQ = useQuery({
    queryKey: ["public-booking-slots", token, activeLocation?.id, dayOffset],
    enabled: !!activeLocation,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_booking_slots", {
        _token: token,
        _location_id: activeLocation!.id,
        _from: isoDatePlus(dayOffset),
        _days: DAYS_PER_VIEW,
      });
      if (error) throw error;
      return (data as unknown as PublicDaySlots[] | null) ?? [];
    },
    staleTime: 30 * 1000,
  });

  const days = useMemo(() => slotsQ.data ?? [], [slotsQ.data]);
  const activeDay = useMemo(
    () => days.find((d) => d.date === selectedDate) ?? days.find((d) => d.slots.length > 0) ?? null,
    [days, selectedDate],
  );

  const maxAdvance = page?.settings.max_advance_days ?? 21;
  const canPageForward = dayOffset + DAYS_PER_VIEW <= maxAdvance;

  function pickLocation(id: string) {
    setLocationId(id);
    setSelectedDate(null);
    setSelectedSlot(null);
    setDayOffset(0);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!activeLocation || !activeDay || !selectedSlot) return;
    setBusy(true);
    try {
      const res = (await submit({
        data: {
          token,
          locationId: activeLocation.id,
          date: activeDay.date,
          start: selectedSlot.start,
          name,
          email,
          phone: phone || undefined,
          note: note || undefined,
          website,
        },
      })) as SubmitBookingResult;
      if (res.ok) {
        setConfirmed(res.booking);
      } else {
        toast.error(ERROR_COPY[res.error] ?? ERROR_COPY.server_error);
        if (res.error === "slot_taken" || res.error === "slot_unavailable") {
          setSelectedSlot(null);
          qc.invalidateQueries({ queryKey: ["public-booking-slots", token] });
        }
      }
    } catch {
      toast.error(ERROR_COPY.server_error);
    } finally {
      setBusy(false);
    }
  }

  // ---- Render states ------------------------------------------------------

  if (pageQ.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center">
          <h1 className="text-xl font-semibold">Booking unavailable</h1>
          <p className="text-sm text-muted-foreground mt-2">
            This booking page is no longer active. Please contact your coach directly.
          </p>
        </Card>
      </div>
    );
  }

  const coachName = page.coach.business_name || page.coach.full_name || "Your coach";

  // ---- Confirmation screen ------------------------------------------------
  if (confirmed) {
    const locLabel = [activeLocation?.name, activeLocation?.city].filter(Boolean).join(", ");
    const title = `Lesson with ${coachName}`;
    const gUrl = googleCalendarUrl({
      date: confirmed.date,
      start: confirmed.start,
      end: confirmed.end,
      title,
      location: locLabel,
      timezone: confirmed.timezone,
    });
    const manageUrl = `${window.location.origin}/booking/${confirmed.manage_token}`;

    function downloadIcs() {
      const ics = icsFileContent({
        date: confirmed!.date,
        start: confirmed!.start,
        end: confirmed!.end,
        title,
        location: locLabel,
        uid: confirmed!.id,
      });
      const blob = new Blob([ics], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "lesson.ics";
      a.click();
      URL.revokeObjectURL(url);
    }

    return (
      <div className="min-h-screen bg-muted/30 p-4 md:p-8">
        <div className="max-w-md mx-auto space-y-4 pt-8">
          <Card className="p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-brand-text">
              <Check className="h-6 w-6" />
            </div>
            <h1 className="mt-4 text-2xl font-bold tracking-tight">
              {confirmed.status === "confirmed" ? "You're booked!" : "Request sent!"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {confirmed.status === "confirmed"
                ? `Your lesson with ${coachName} is confirmed.`
                : `${coachName} will confirm your lesson shortly.`}{" "}
              A confirmation email is on its way to{" "}
              <strong className="text-foreground">{email}</strong>.
            </p>
            <div className="mt-5 rounded-lg border bg-muted/40 p-4 text-left text-sm space-y-1">
              <p>
                <span className="font-semibold">When:</span> {fmtBookingDate(confirmed.date)},{" "}
                {fmtSlotTime(confirmed.start)} – {fmtSlotTime(confirmed.end)}
              </p>
              {locLabel && (
                <p>
                  <span className="font-semibold">Where:</span> {locLabel}
                </p>
              )}
            </div>
            {confirmed.status === "confirmed" && (
              <div className="mt-5 flex flex-col gap-2">
                <Button asChild>
                  <a href={gUrl} target="_blank" rel="noreferrer">
                    Add to Google Calendar
                  </a>
                </Button>
                <Button variant="outline" onClick={downloadIcs}>
                  Download .ics (Apple / Outlook)
                </Button>
              </div>
            )}
            <p className="mt-5 text-xs text-muted-foreground">
              Need to cancel?{" "}
              <a href={manageUrl} className="font-medium text-brand-text hover:underline">
                Manage your booking
              </a>
            </p>
          </Card>
          <p className="text-center text-xs text-muted-foreground">Powered by CoachQuick</p>
        </div>
      </div>
    );
  }

  // ---- Booking flow -------------------------------------------------------
  return (
    <div className="min-h-screen bg-muted/30 p-4 md:p-8">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center pt-4">
          <p className="text-sm text-muted-foreground">Book a lesson with</p>
          <h1 className="text-2xl font-semibold mt-1">{coachName}</h1>
          {page.settings.page_headline && (
            <p className="mt-2 text-sm font-medium">{page.settings.page_headline}</p>
          )}
          {page.settings.page_message && (
            <p className="mt-1 text-sm text-muted-foreground">{page.settings.page_message}</p>
          )}
        </div>

        {/* Step 1 — Location */}
        <Card className="p-4">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-brand-text" /> Where
          </h2>
          {page.locations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No bookable locations right now — check back soon.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {page.locations.map((l) => {
                const active = activeLocation?.id === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => pickLocation(l.id)}
                    className={`rounded-lg border-2 p-3 text-left transition-colors ${
                      active
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <p className="text-sm font-semibold">{l.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {[l.city, l.state].filter(Boolean).join(", ") ||
                        (l.indoor ? "Indoor" : "Outdoor")}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </Card>

        {/* Step 2 — Day & time */}
        {activeLocation && (
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold flex items-center gap-2">
                <CalendarCheck2 className="h-4 w-4 text-brand-text" /> When
              </h2>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={dayOffset === 0}
                  onClick={() => {
                    setDayOffset((o) => Math.max(0, o - DAYS_PER_VIEW));
                    setSelectedDate(null);
                    setSelectedSlot(null);
                  }}
                  aria-label="Earlier dates"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={!canPageForward}
                  onClick={() => {
                    setDayOffset((o) => o + DAYS_PER_VIEW);
                    setSelectedDate(null);
                    setSelectedSlot(null);
                  }}
                  aria-label="Later dates"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {slotsQ.isLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking availability…
              </div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1.5">
                  {days.map((d) => {
                    const [y, mo, dd] = d.date.split("-").map(Number);
                    const dt = new Date(y, mo - 1, dd);
                    const isActive = activeDay?.date === d.date;
                    const has = d.slots.length > 0;
                    return (
                      <button
                        key={d.date}
                        type="button"
                        disabled={!has}
                        onClick={() => {
                          setSelectedDate(d.date);
                          setSelectedSlot(null);
                        }}
                        className={`rounded-lg border-2 py-2 text-center transition-colors ${
                          isActive && has
                            ? "border-primary bg-primary text-primary-foreground"
                            : has
                              ? "border-border bg-card hover:border-primary/40"
                              : "border-transparent bg-muted/40 text-muted-foreground/50"
                        }`}
                      >
                        <div className="text-[10px] font-medium uppercase">
                          {WEEKDAY_LABELS[dt.getDay()]}
                        </div>
                        <div className="text-sm font-bold">{dt.getDate()}</div>
                        <div className="text-[10px]">{has ? `${d.slots.length}` : "–"}</div>
                      </button>
                    );
                  })}
                </div>

                {activeDay ? (
                  <>
                    <p className="mt-4 text-xs font-medium text-muted-foreground">
                      {fmtBookingDate(activeDay.date)} · times shown in{" "}
                      {page.settings.timezone.replace(/_/g, " ")}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {activeDay.slots.map((s) => {
                        const active = selectedSlot?.start === s.start;
                        return (
                          <button
                            key={s.start}
                            type="button"
                            onClick={() => setSelectedSlot(s)}
                            className={`rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-colors ${
                              active
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border bg-card hover:border-primary/40"
                            }`}
                          >
                            {fmtSlotTime(s.start)}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="mt-4 text-sm text-muted-foreground">
                    No open times in this range — try later dates.
                  </p>
                )}
              </>
            )}
          </Card>
        )}

        {/* Step 3 — Details */}
        {activeLocation && activeDay && selectedSlot && (
          <Card className="p-4">
            <h2 className="text-sm font-semibold mb-1">Your details</h2>
            <p className="text-xs text-muted-foreground mb-3">
              {fmtBookingDate(activeDay.date)} at {fmtSlotTime(selectedSlot.start)} ·{" "}
              {activeLocation.name}
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="bk-name">Full name</Label>
                  <Input
                    id="bk-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="bk-email">Email</Label>
                  <Input
                    id="bk-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="bk-phone">Phone (optional)</Label>
                <Input
                  id="bk-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="bk-note">Anything the coach should know? (optional)</Label>
                <Textarea
                  id="bk-note"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              {/* Honeypot — hidden from humans */}
              <div className="absolute -left-[9999px] top-auto" aria-hidden="true">
                <label htmlFor="bk-website">Website</label>
                <input
                  id="bk-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <Button type="submit" className="w-full" size="lg" disabled={busy}>
                {busy
                  ? "Booking…"
                  : page.settings.auto_confirm
                    ? `Book ${fmtSlotTime(selectedSlot.start)} on ${fmtBookingDate(activeDay.date, { weekday: "short", month: "short" })}`
                    : "Request this time"}
              </Button>
              {!page.settings.auto_confirm && (
                <p className="text-center text-xs text-muted-foreground">
                  {coachName} will confirm your request by email.
                </p>
              )}
            </form>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground pt-4">Powered by CoachQuick</p>
      </div>
    </div>
  );
}
